'use client';

import { Container } from '@packages/shared/src/components/Container/Container';
import { Card } from '@packages/shared/src/components/Card/Card';
import type { JSX } from 'react';
import { CardContent } from '@packages/shared/src/components/Card';
import { ComponentRecognizer } from '../../components/ComponentRecognizer/ComponentRecognizer';
import type { CmsDataJsonApi } from '../../../interfaces/jsonapi';
import { SnippetTypeList } from '../../../interfaces/contentSnippet';
import { registerFlexibleComponents } from '../../components/ComponentRecognizer/registerFlexibleComponents';

// register the dynamic components
registerFlexibleComponents();

export type ServiceLayerItemProps = {
  /** json data used to render banner */
  serviceLayerData: CmsDataJsonApi;
};

/**
 * This component displays Service Layer Content on Product Detailview Pages
 *
 * @param serviceLayerData Object contains data
 * @returns JSX.Element to be placed in PageLayout
 */
const ServiceLayerItem = ({ serviceLayerData }: ServiceLayerItemProps): JSX.Element => {
  if (!serviceLayerData.data) {
    throw new Error('No data found');
  }
  return (
    <Container
      maxWidth="md"
      sx={{
        padding: 2,
        fontSize: 'body2.fontSize',
        'ul, ol': {
          paddingLeft: 0,
          li: {
            '&.type-1, &.type-2, &.type-3': {
              listStyleType: 'none',
            },
            '&.type-1': {
              marginLeft: '-1rem',
              '&:before': {
                content: '" "',
                background: `transparent url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDMyIDMyIj48cGF0aCBkPSJNNy43MzM5OTc1MSwxNS45ODczMDEgQzcuNTM5MTg2NTIsMTUuNzkwNTkwOSA3LjIyNzIyNTcyLDE1Ljc4NjY2MjcgNy4wMzI0NzA3NywxNS45ODMzMTYzIEw1LjE0MzgyMzExLDE3Ljg5MDM3NiBDNC45NTExOTE1OCwxOC4wODQ4ODU0IDQuOTUyMDM2MDgsMTguNDAxMTAwMyA1LjE0NjUxODE4LDE4LjU5NzQ3ODQgTDExLjM0MDE1NTksMjQuODUxNDk2NiBDMTEuNTM0Mjc1OSwyNS4wNDc1MDkgMTEuODU1NDA5MiwyNS4wNTAyMzA4IDEyLjA1OTkxMzYsMjQuODU1MjA0OCBMMjYuODQwNzM3NiwxMC43NTk0NDc5IEMyNy4wNDQxMjg4LDEwLjU2NTQ4MzUgMjcuMDU0OTc0NywxMC4yNTI3MDc0IDI2Ljg2MDIxOTgsMTAuMDU2MDUzOCBMMjQuOTcxNTcyMSw4LjE0ODk5NDExIEMyNC43Nzg5NDA2LDcuOTU0NDg0NjYgMjQuNDU5Mjg0NSw3Ljk1MDg4ODgxIDI0LjI2MzQzMjksOC4xMzU0NjU2OCBMMTEuNjkxNjQxMiwxOS45ODM1MjcgTDcuNzMzOTk3NTEsMTUuOTg3MzAxIFoiIGNsYXNzPSJwYXRoLTEiLz48L3N2Zz4K') center center no-repeat`,
                flexShrink: 0,
                flexBasis: '1rem',
                width: { sm: '1.25rem', md: '1.5rem' },
                height: { sm: '0.94rem', md: '1.5rem' },
                marginRight: 1,
                display: 'inline-block',
                verticalAlign: 'middle',
              },
            },
            '&.type-3': {
              paddingLeft: { sm: 2, md: 1 },
            },
            ':last-child': {
              paddingBottom: 1,
            },
          },
        },
      }}
    >
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          {serviceLayerData.data.map(
            (item, index): JSX.Element | false =>
              item.type === SnippetTypeList.serviceLayerDv &&
              item.field_paragraph && (
                // The unique key is important from a performance point of view so that React doesn't have to render everything.
                // In this case it doesn't matter if only index or key is passed. The list is as it is and done.
                // eslint-disable-next-line react/no-array-index-key
                <ComponentRecognizer key={index} item={item.field_paragraph} />
              ),
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

// eslint-disable-next-line import/no-default-export
export default ServiceLayerItem;
